import { memo, useEffect, useRef, useState } from 'react';
import type { DemoScript, FingerKey, Fx, Level, Pt, RoundProps, ViewProps } from '../kit.js';
import { BOARD, BoardFrame, INK, WARN, easier, q, say, seeded, thirtyGame, useRound, useSvgId } from '../kit.js';
import type { Era } from '../../art.js';
import { GROUND, SPECKS, clamp, demoRound, feedbackTo, playDemo, wave } from './garden.js';
import type { Feedback } from './garden.js';

/**
 * BIOTECHNICIAN · MUSHROOMS IN THE DARK — slide to light the dark, tap each mushroom you see.
 *
 * The classic Forage, grown up: the board is a dark cave floor and the finger carries a candle a
 * little above it; whatever grows near the flame shows. A tap on a mushroom the candle lights
 * picks it (the phone gives a little buzz); a tap in the dark only moves the candle, so nothing
 * here is hurried. Round 1 has four to find, rounds 2 and 3 five; each round the candle's circle
 * is smaller. (Poisonous look-alikes among them — the same shape, but plainly spotted once lit —
 * the old round 3, are still in the rules; picking one is the mistake. No round grows them since
 * 6 Oct 2026.)
 */

export interface Mushroom {
  id: number;
  /** Where it grows, as fractions of the board. */
  x: number;
  y: number;
  bad: boolean;
  /** A little variety: size and lean. */
  size: number;
  lean: number;
}

export interface Patch {
  good: number;
  bad: number;
  /** The candle's reach, as a fraction of the board's width. */
  light: number;
}

/** Easier since 6 Oct 2026 (kit `easier`): round 1 as before, round 2 halfway to the old round 2, round 3 the old round 2. */
export const PATCHES: readonly Patch[] = easier(
  [
    { good: 4, bad: 0, light: 0.3 },
    { good: 5, bad: 0, light: 0.24 },
  ],
);

/** A tap this close to a lit mushroom picks it, as a fraction of the board's width. */
export const REACH = 0.1;
/** The board's height against its width, for keeping mushrooms apart: square (kit `BOARD`). */
export const ASPECT = BOARD.h / BOARD.w;
/** Where the candle stands before the first touch. */
export const CANDLE_START = { x: 0.5, y: 0.9 };
/** How far above the finger the candle's light falls, in board units, so the finger never hides what it shows. */
const LIFT = 34;

const W = BOARD.w;
const H = BOARD.h;
/** A picked mushroom goes up into the basket over this long; a look-alike wilts over the longer. */
const PICK_MS = 500;
const WILT_MS = 900;
/** "Poison" shows this long after a look-alike is picked. */
const POISON_MS = 1_200;

/** Distance between two points given as fractions, in board widths. */
export const apart = (a: { x: number; y: number }, b: { x: number; y: number }) => Math.hypot(a.x - b.x, (a.y - b.y) * ASPECT);

/**
 * A round's patch: the good ones and the look-alikes, spread out so no tap can reach two, and all
 * out of the candle's first light so the first one has to be looked for.
 */
export function growPatch(rand: () => number, level: Level): Mushroom[] {
  const p = PATCHES[level]!;
  const total = p.good + p.bad;
  for (let attempt = 0; attempt < 40; attempt += 1) {
    const out: Mushroom[] = [];
    let guard = 0;
    while (out.length < total && guard < 800) {
      guard += 1;
      const c = { x: 0.1 + rand() * 0.8, y: 0.14 + rand() * 0.66 };
      if (apart(c, CANDLE_START) < p.light + 0.06) continue;
      if (out.some((m) => apart(m, c) < 0.21)) continue;
      out.push({ id: out.length, ...c, bad: false, size: 1 + rand() * 0.2, lean: (rand() - 0.5) * 16 });
    }
    if (out.length < total) continue;
    // Which are the look-alikes: dealt among them at random.
    const order = out.map((m) => m.id);
    for (let i = order.length - 1; i > 0; i -= 1) {
      const j = Math.floor(rand() * (i + 1));
      [order[i], order[j]] = [order[j]!, order[i]!];
    }
    for (const id of order.slice(0, p.bad)) out[id]!.bad = true;
    return out;
  }
  // A fallback that can't fail: a loose grid.
  return Array.from({ length: total }, (_, i) => ({
    id: i,
    x: 0.18 + (i % 4) * 0.21,
    y: 0.18 + Math.floor(i / 4) * 0.3,
    bad: i >= p.good,
    size: 1,
    lean: 0,
  }));
}

/** The mushroom a tap at `tap` picks, lit by a candle standing at `light`, or null. */
export function pickAt(mushrooms: readonly Mushroom[], gone: ReadonlySet<number>, tap: { x: number; y: number }, light: { x: number; y: number }, radius: number): Mushroom | null {
  let best: Mushroom | null = null;
  let bestD = REACH;
  for (const m of mushrooms) {
    if (gone.has(m.id) || apart(m, light) > radius * 0.88) continue;
    const d = apart(m, tap);
    if (d <= bestD) {
      best = m;
      bestD = d;
    }
  }
  return best;
}

/** Everything the cave shows. */
export interface MushroomsView {
  /** The game clock, ms. */
  t: number;
  /** Where the candle's light falls (units), and how far it reaches. */
  l: readonly [x: number, y: number];
  r: number;
  /** The mushrooms: across, down (units), size, lean (degrees), a look-alike (1), and ms since picked (−1: still growing). */
  m: readonly (readonly [x: number, y: number, size: number, lean: number, bad: 0 | 1, gone: number])[];
  /** Picked, and how many to pick. */
  n: number;
  of: number;
  /** Look-alikes grow this round. */
  poison: boolean;
  /** 0 not yet touched, 1 looking, 2 a look-alike just picked, 3 the basket full. */
  st: 0 | 1 | 2 | 3;
  fx?: Fx | undefined;
}

/** A round in the cave, driven by a finger (a guest's or the how-to's). */
export function mushroomsRound(level: Level, mushrooms: readonly Mushroom[], fb: Feedback) {
  const P = PATCHES[level]!;
  let light = CANDLE_START;
  let touched = false;
  let dragging = false;
  /** Picked (or thrown away), and when. */
  const gone = new Map<number, number>();
  let good = 0;
  let badAt = -1e9;
  let won = false;
  /** Where the candle stands for a finger at `p`: a little above it. */
  const candleFor = (p: Pt) => ({ x: clamp(p.x / W), y: clamp((p.y - LIFT) / H) });

  return {
    input: {
      /** A touch: picks the lit mushroom under it, if any ('good' or 'bad'), and the candle goes there. */
      down(p: Pt, now: number): 'good' | 'bad' | null {
        if (won) return null;
        const tap = { x: p.x / W, y: p.y / H };
        // A tap on a mushroom the candle already shows picks it; anywhere else the candle goes there.
        const hit = touched ? pickAt(mushrooms, new Set(gone.keys()), tap, light, P.light) : null;
        dragging = true;
        light = candleFor(p);
        touched = true;
        if (!hit) return null;
        gone.set(hit.id, now);
        if (hit.bad) {
          badAt = now;
          fb.miss();
          return 'bad';
        }
        good += 1;
        fb.hit();
        fb.progress(good / P.good);
        if (good >= P.good) {
          won = true;
          fb.win();
        }
        return 'good';
      },
      move(p: Pt) {
        if (dragging && !won) light = candleFor(p);
      },
      up() {
        dragging = false;
      },
    },
    view(now: number, fx: Fx | undefined): MushroomsView {
      return {
        t: q(now),
        l: [q(light.x * W), q(light.y * H)],
        r: q(P.light * W),
        m: mushrooms.flatMap((m) => {
          const at = gone.get(m.id);
          const since = at === undefined ? -1 : q(now - at);
          return since > WILT_MS ? [] : [[q(m.x * W), q(m.y * H), q(m.size, 2), q(m.lean), m.bad ? 1 : 0, since] as const];
        }),
        n: good,
        of: P.good,
        poison: P.bad > 0,
        st: won ? 3 : now - badAt < POISON_MS ? 2 : touched ? 1 : 0,
        fx,
      };
    },
  };
}

/** The cave, from the view state alone. */
function View({ view, era, accent, lang, input }: ViewProps<MushroomsView>) {
  const id = useSvgId();
  // A demo glides every number between its keys: counts and states are read rounded.
  const st = Math.round(view.st);
  const [lx, ly] = view.l;
  const R = view.r;
  const status =
    st === 3
      ? { text: say(lang, '✓ BASKET FULL', '✓ KURVEN ER FULL'), tone: 'ok' as const }
      : st === 2
        ? { text: say(lang, 'POISON — NOT THE SPOTTED', 'GIFTIG – IKKE DE PRIKKETE'), tone: 'bad' as const }
        : st === 0
          ? { text: say(lang, 'SLIDE TO LIGHT THE DARK', 'SKYV FOR Å LYSE OPP') }
          : {
              text: say(lang, 'PICKED', 'PLUKKET'),
              count: [Math.round(view.n), view.of] as const,
              hint: view.poison ? say(lang, 'LEAVE THE SPOTTED ONES', 'LA DE PRIKKETE STÅ') : say(lang, 'TAP WHAT THE LIGHT SHOWS', 'TRYKK PÅ DET DU SER'),
            };

  return (
    <BoardFrame era={era} accent={accent} status={status} fx={view.fx} input={input}>
      <defs>
        <MushroomDefs id={id} era={era} />
        {/* the dark, with the candle's circle in it */}
        <radialGradient id={`${id}dark`} gradientUnits="userSpaceOnUse" cx={lx} cy={ly} r={R}>
          <stop offset="0" stopColor={WARN} stopOpacity=".16" />
          <stop offset=".45" stopColor={WARN} stopOpacity=".05" />
          <stop offset=".84" stopColor={INK.onFill} stopOpacity=".9" />
          <stop offset=".96" stopColor={INK.onFill} stopOpacity=".99" />
          <stop offset="1" stopColor={INK.onFill} />
        </radialGradient>
      </defs>
      <CaveFloorArt era={era} id={id} />
      {view.m.map(([x, y, size, lean, bad, since]) => {
        const picked = since >= 0;
        let dy = 0;
        let sc = size;
        let o = 1;
        if (picked && !bad) {
          // Picked: up and into the basket.
          const u = clamp(since / PICK_MS);
          dy = -u * 26;
          sc = size * (1 - u);
          o = 1 - u;
        } else if (picked) {
          // A look-alike picked: it wilts into the dark.
          const u = clamp(since / WILT_MS);
          dy = u * 6;
          o = 1 - u;
        }
        return (
          <g key={`${x},${y}`} transform={`translate(${x} ${(y + dy).toFixed(1)})`} opacity={o.toFixed(2)}>
            <g transform={`scale(${sc.toFixed(3)}) rotate(${lean} 0 14)`}>
              <MushroomArt bad={bad === 1} id={id} wilted={picked && bad === 1} />
            </g>
            {picked &&
              bad === 1 &&
              [0, 1, 2, 3].map((i) => (
                <circle key={i} cx={(i - 1.5) * 7} cy={-10 - clamp(since / WILT_MS) * 26 - i * 3} r={1.6 + i * 0.3} fill="#c8d0a0" opacity={(0.6 * (1 - clamp(since / WILT_MS))).toFixed(2)} />
              ))}
          </g>
        );
      })}
      <rect x="0" y="0" width={W} height={H} fill={`url(#${id}dark)`} />

      {/* the candle, just above the fingertip, its light a little further up */}
      <g transform={`translate(${lx} ${ly + 32})`}>
        <ellipse cx="0" cy="11" rx="10" ry="2.6" fill="#6b4a2a" />
        <rect x="-5" y="-2" width="10" height="13" rx="2" fill="#f2e6c8" />
        <rect x="-5" y="-2" width="3" height="13" rx="1.5" fill="#ffffff" opacity=".45" />
        <path d="M0 -2 L0 -6" stroke="#2a1a0a" strokeWidth="1" />
        <g transform={`rotate(${(-2 + 5 * wave(view.t, 1_600)).toFixed(1)} 0 -6)`}>
          <path d="M0 -21 C 4 -15, 4 -9, 0 -6 C -4 -9, -4 -15, 0 -21 Z" fill={WARN} />
          <path d="M0 -17 C 2 -13, 2 -9, 0 -7 C -2 -9, -2 -13, 0 -17 Z" fill="#fff4d0" />
        </g>
      </g>
    </BoardFrame>
  );
}

/** The cave floor the candle shows: earth, stones, roots from above, moss. Drawn once, all of it; the dark hides it. */
function CaveFloor({ era, id }: { era: Era; id: string }) {
  const g = GROUND[era];
  return (
    <g>
      <rect x="0" y="0" width={W} height={H} fill={`url(#${id}floor)`} />
      {/* roots hanging from the roof */}
      {[0.12, 0.34, 0.63, 0.86].map((x, i) => (
        <path
          key={x}
          d={`M${x * W} 0 q ${i % 2 ? 6 : -6} ${H * 0.08} ${i % 2 ? -2 : 3} ${H * 0.16} q -4 ${H * 0.05} ${i % 2 ? 5 : -4} ${H * 0.1}`}
          fill="none"
          stroke={g.stemDark}
          strokeWidth="2.2"
          strokeLinecap="round"
          opacity=".8"
        />
      ))}
      {/* stones */}
      {SPECKS.slice(0, 9).map((p, i) => {
        const x = p.x * W;
        const y = 0.2 * H + p.y * 0.75 * H;
        const r = 5 + p.r * 5;
        return (
          <g key={i}>
            <ellipse cx={x + 1.5} cy={y + r * 0.45} rx={r * 1.1} ry={r * 0.35} fill="#000" opacity=".35" />
            <path d={`M${x - r} ${y + r * 0.3} Q ${x - r} ${y - r * 0.6} ${x} ${y - r * 0.7} Q ${x + r * 1.1} ${y - r * 0.5} ${x + r} ${y + r * 0.3} Z`} fill={`url(#${id}stone)`} />
          </g>
        );
      })}
      {/* moss and pebbles */}
      {SPECKS.map((p, i) => (
        <g key={`m${i}`}>
          <ellipse cx={(1 - p.x) * W} cy={p.y * H} rx={6 + p.r * 4} ry={2 + p.r} fill={g.leaf} opacity=".22" />
          <circle cx={p.y * W} cy={(1 - p.x) * H} r={p.r} fill={g.fleck} />
        </g>
      ))}
    </g>
  );
}

function MushroomDefs({ id, era }: { id: string; era: Era }) {
  const g = GROUND[era];
  // In the city's era the good ones glow a little under the light.
  const good = era === 'neon' ? ['#b4fbff', '#2aa8c8', '#0e4a5e'] : ['#f0b070', '#b5652a', '#5e2e10'];
  const bad = era === 'neon' ? ['#ffb4e0', '#d03a90', '#5e0e38'] : ['#ff9a70', '#c8402a', '#5e1208'];
  return (
    <>
      <radialGradient id={`${id}floor`} cx=".5" cy=".6" r=".8">
        <stop offset="0" stopColor={g.soilTop} />
        <stop offset="1" stopColor={g.soilBottom} />
      </radialGradient>
      <linearGradient id={`${id}stone`} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor={g.rockHi} />
        <stop offset="1" stopColor={g.rock} />
      </linearGradient>
      <radialGradient id={`${id}good`} cx=".38" cy=".25" r=".85">
        <stop offset="0" stopColor={good[0]} />
        <stop offset=".55" stopColor={good[1]} />
        <stop offset="1" stopColor={good[2]} />
      </radialGradient>
      <radialGradient id={`${id}bad`} cx=".38" cy=".25" r=".85">
        <stop offset="0" stopColor={bad[0]} />
        <stop offset=".55" stopColor={bad[1]} />
        <stop offset="1" stopColor={bad[2]} />
      </radialGradient>
      <linearGradient id={`${id}stem`} x1="0" y1="0" x2="1" y2="0">
        <stop offset="0" stopColor="#c9b48c" />
        <stop offset=".4" stopColor="#fbf3e0" />
        <stop offset="1" stopColor="#a8916a" />
      </linearGradient>
    </>
  );
}

/**
 * One mushroom, about 44 units, its foot at (0, 14). The look-alike has the same shape; it wears
 * bold white spots and a ring on its stem.
 */
export function MushroomArt({ bad, id, wilted = false }: { bad: boolean; id: string; wilted?: boolean }) {
  return (
    <g style={wilted ? { filter: 'grayscale(1) brightness(.6)' } : undefined}>
      <ellipse cx="1" cy="14.5" rx="14" ry="3" fill="#000" opacity=".45" />
      <path d="M-6 14 Q -7.5 6 -4.5 -2 L4.5 -2 Q 7.5 6 6 14 Q 0 16 -6 14 Z" fill={`url(#${id}stem)`} stroke="#6e5a3a" strokeWidth=".6" />
      {bad && <path d="M-6.4 3 Q 0 7 6.4 3 L7.4 5.6 Q 0 10 -7.4 5.6 Z" fill="#f4ecd8" stroke="#8a7a5a" strokeWidth=".5" />}
      {/* under the cap: the gills */}
      <path d="M-17 -1 Q 0 5 17 -1 Q 0 1.5 -17 -1 Z" fill="#e8d6b0" />
      {[-12, -8, -4, 0, 4, 8, 12].map((x) => (
        <line key={x} x1={x * 0.9} y1="-0.4" x2={x} y2="1.6" stroke="#a8916a" strokeWidth=".5" />
      ))}
      <path d="M-18 -1 Q -17 -19 0 -20 Q 17 -19 18 -1 Q 0 3 -18 -1 Z" fill={`url(#${id}${bad ? 'bad' : 'good'})`} stroke="#3a1a08" strokeWidth=".7" />
      <path d="M-11 -12 Q -6 -17 1 -17" fill="none" stroke="#ffffff" strokeOpacity=".5" strokeWidth="2" strokeLinecap="round" />
      {bad && (
        <g fill="#ffffff" stroke="#5e1208" strokeWidth=".5">
          <circle cx="-9" cy="-9" r="2.8" />
          <circle cx="0" cy="-14" r="2.4" />
          <circle cx="8" cy="-8" r="3" />
          <circle cx="-2" cy="-6" r="2" />
          <circle cx="12" cy="-3.5" r="1.8" />
          <circle cx="-13" cy="-3.2" r="1.7" />
        </g>
      )}
    </g>
  );
}

// The floor and the mushrooms stay the same from frame to frame: drawn once, not sixty times a second.
const CaveFloorArt = memo(CaveFloor);

function Board(props: RoundProps) {
  const { level, lang } = props;
  const fb = useRef<Feedback | null>(null);
  const [game] = useState(() => {
    const rand = seeded(Date.now() + level * 7919);
    return mushroomsRound(level, growPatch(rand, level), feedbackTo(fb));
  });
  const round = useRound(props);
  fb.current = round;
  useEffect(() => {
    if (props.paused) game.input.up();
  }, [props.paused, game]);

  return (
    <View
      view={round.show(game.view(round.now, round.fx))}
      era={props.era}
      accent={props.accent}
      lang={lang}
      input={{
        label: say(lang, 'Slide to light the dark, tap a mushroom', 'Skyv for å lyse opp, trykk på en sopp'),
        down: (p) => {
          if (round.live && game.input.down(p, round.time()) === 'good') navigator.vibrate?.(15);
        },
        move: (p) => round.live && game.input.move(p),
        up: () => game.input.up(),
      }}
    />
  );
}

/** The how-to's patch: round 1's four, laid by hand. */
const DEMO_PATCH: readonly Mushroom[] = [
  { id: 0, x: 0.3, y: 0.62, bad: false, size: 1.1, lean: -4 },
  { id: 1, x: 0.75, y: 0.6, bad: false, size: 1, lean: 5 },
  { id: 2, x: 0.25, y: 0.24, bad: false, size: 1.15, lean: 3 },
  { id: 3, x: 0.72, y: 0.22, bad: false, size: 1.05, lean: -6 },
];

/**
 * The how-to, played by the rules themselves: the finger slides the candle up until a mushroom
 * shows in its light, lifts, and taps it; then slides on to the next one and taps that too.
 */
function demoForage(): DemoScript<MushroomsView> {
  const ms = 7_000;
  const finger: FingerKey[] = [
    { at: 0, x: 200, y: 304, hide: true },
    { at: 300, x: 200, y: 304, hide: false },
    { at: 700, x: 164, y: 304 },
    { at: 900, x: 164, y: 304, down: true },
    { at: 1_800, x: 100, y: 236, ease: 'inOut' },
    { at: 1_900, x: 100, y: 236, down: false },
    { at: 2_400, x: 96, y: 200 },
    { at: 2_600, x: 96, y: 200, down: true },
    { at: 2_720, x: 96, y: 200, down: false },
    { at: 3_200, x: 150, y: 236 },
    { at: 3_400, x: 150, y: 236, down: true },
    { at: 4_400, x: 240, y: 228, ease: 'inOut' },
    { at: 4_500, x: 240, y: 228, down: false },
    { at: 5_000, x: 240, y: 194 },
    { at: 5_200, x: 240, y: 194, down: true },
    { at: 5_320, x: 240, y: 194, down: false },
    { at: 6_000, x: 280, y: 312, hide: true },
  ];
  const round = demoRound();
  const game = mushroomsRound(0, DEMO_PATCH, round);
  const keys = playDemo(
    ms,
    finger,
    round,
    { input: game.input, step: () => {}, view: (now) => game.view(now, round.fx()) },
    ({ t: _t, l: _l, m, fx: _fx, ...parts }) => ({ ...parts, m: m.map(([x, y, , , bad, since]) => [x, y, bad, since >= 0]) }),
  );
  return {
    ms,
    keys,
    finger,
    steps: [
      { at: 0, en: 'SLIDE TO MOVE THE LIGHT', no: 'SKYV FOR Å FLYTTE LYSET' },
      { at: 2_000, en: 'TAP A MUSHROOM YOU SEE', no: 'TRYKK PÅ EN SOPP DU SER' },
      { at: 3_300, en: 'FIND THEM ALL IN THE DARK', no: 'FINN ALLE I MØRKET' },
    ],
  };
}

export const game = thirtyGame<MushroomsView>({
  Board,
  View,
  demo: demoForage(),
  words: {
    en: {
      instruction: 'Slide to light the dark, tap each mushroom you see. Less light each round.',
      howMove: 'Slide your finger to carry the candle. Lift it and tap a lit mushroom to pick it.',
    },
    no: {
      instruction: 'Skyv for å lyse opp mørket, trykk på hver sopp du ser. Mindre lys hver runde.',
      howMove: 'Skyv fingeren for å bære lyset. Løft den og trykk på en opplyst sopp for å plukke den.',
    },
  },
});
